// Sticky single-select picker above the non-Overview tabs, plus the
// three concrete pickers (agent / scorecard / tag). Shared behavior:
// String-coerced value matching (cohort clicks stringify ids),
// auto-pick of the first option on mount or when the held value goes
// stale (org switch), and a labeled empty state.

import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'

import Select from '@/components/forms/Select'

export const SectionEntityPicker = ({
  entityNoun, // display label ("Scorecard", "Agent", ...)
  inputId, // stable id for the input for a11y
  options, // flat [{value, label}]; grouped shapes should be flattened by the caller
  pickedEntity, // currently held pick or null
  onPick, // called with {value, label}
  emptyHint = null, // rendered when options is empty (skip entire block if null)
  scopeHint = null, // one-line context under the input
}) => {
  const matchingOption = pickedEntity
    ? options.find((option) => String(option.value) === String(pickedEntity.value))
    : null

  useEffect(() => {
    if (isEmpty(options)) {
      return
    }
    if (!pickedEntity || !matchingOption) {
      const first = options[0]
      onPick({ value: first.value, label: first.label })
    }
  }, [pickedEntity, options, matchingOption, onPick])

  if (isEmpty(options)) {
    if (isNil(emptyHint)) {
      return null
    }
    return (
      <div className="section-picker">
        <label htmlFor={inputId}>{entityNoun}:</label>
        <span className="section-picker-hint">{emptyHint}</span>
      </div>
    )
  }

  return (
    <div className="section-picker">
      <label htmlFor={inputId}>{entityNoun}:</label>
      <div className="section-picker-select">
        <Select
          inputId={inputId}
          options={options}
          value={matchingOption?.value ?? null}
          onChange={(option) => onPick({ value: option.value, label: option.label })}
          placeholder={`Search ${entityNoun.toLowerCase()}s…`}
          isClearable={false}
        />
      </div>
      {scopeHint && <span className="section-picker-hint">{scopeHint}</span>}
    </div>
  )
}

// ---- Concrete pickers (one per scope tab) ----

export const SectionAgentPicker = ({ pickedEntity, onPick }) => {
  const options = useSelector((state) => state.qaCopilot.data.agents || [])
  return (
    <SectionEntityPicker
      entityNoun="Agent"
      inputId="section-agent-picker-input"
      options={options}
      pickedEntity={pickedEntity}
      onPick={onPick}
      emptyHint="No agents available."
      scopeHint="Charts below are scoped to this agent."
    />
  )
}

// Scorecards arrive as either {value,label} (reducer-normalized) or
// {sid,name} (direct API responses); normalize to {value,label}.
const normalizeScorecard = (scorecard) => ({
  value: scorecard.value || scorecard.sid,
  label: scorecard.label || scorecard.name,
})

export const SectionScorecardPicker = ({ pickedEntity, onPick }) => {
  const allScorecards = useSelector((state) => state.qaCopilot.data.scorecards || [])
  const options = useMemo(() => allScorecards.map(normalizeScorecard), [allScorecards])
  return (
    <SectionEntityPicker
      entityNoun="Scorecard"
      inputId="section-scorecard-picker-input"
      options={options}
      pickedEntity={pickedEntity}
      onPick={onPick}
      scopeHint="Charts below are scoped to this scorecard."
    />
  )
}

// `data.tags` arrives grouped ([{label, options: [...]}]); flatten so
// the shared Select value-finder (top-level walk only) can match.
const flattenTagOptions = (tags) => {
  if (isEmpty(tags)) {
    return []
  }
  return tags.flatMap((entry) => entry?.options || [entry]).filter(Boolean)
}

export const SectionTagPicker = ({ pickedEntity, onPick }) => {
  const allTags = useSelector((state) => state.qaCopilot.data.tags || [])
  const options = useMemo(() => flattenTagOptions(allTags), [allTags])
  return (
    <SectionEntityPicker
      entityNoun="Tag"
      inputId="section-tag-picker-input"
      options={options}
      pickedEntity={pickedEntity}
      onPick={onPick}
      emptyHint="No tags available."
      scopeHint="Charts below are scoped to this tag."
    />
  )
}
